import React from 'react';
import { ProgramLink, ButtonLink, InlineLink } from '../shared/AcademicLink';

const CollegeGuidanceCounseling = () => {
  const objectives = [
    {
      title: "Problem Prevention",
      description: "Prevent problems and promote optimal human development",
      icon: "🛡️"
    },
    {
      title: "Decision Making",
      description: "Help counselees learn the decision-making process",
      icon: "🎯"
    },
    {
      title: "Behavioral Change",
      description: "Help people alter their maladaptive behavior",
      icon: "🔄"
    },
    {
      title: "Research Methods",
      description: "Apply scientific research methods to find improved ways of helping counselees",
      icon: "📊"
    }
  ];

  const admissionRequirements = [
    {
      title: "Academic Background",
      requirements: [
        "Bachelor's degree with 15 units of required psychology subjects",
        "College GPA equivalent to 3.0 on a 4.0 scale"
      ],
      icon: "🎓"
    },
    {
      title: "Prerequisites",
      requirements: [
        "General Psychology",
        "Developmental Psychology",
        "Social Psychology",
        "Theories of Personality",
        "Abnormal Psychology"
      ],
      icon: "📚"
    },
    {
      title: "Admission Process",
      requirements: [
        "Passing the admissions test",
        "Interview with school chairperson",
        "English proficiency requirement for non-native speakers"
      ],
      icon: "✅"
    }
  ];

  const availableDegrees = [
    {
      title: "Master of Science in School Guidance and Counseling",
      path: "/academics/ms_guidance_counseling",
      description: "Specialized training in school counseling and guidance services",
      icon: "🎓"
    },
    {
      title: "Master of Science in General Counseling",
      path: "/academics/ms_counseling",
      description: "Comprehensive counseling education for various professional settings",
      icon: "💼"
    },
    {
      title: "Master of Arts in Christian Counseling and Psychology",
      path: "/academics/ma_christian_counseling",
      description: "Integration of Christian principles with counseling psychology",
      icon: "🤝"
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-primary-600 to-primary-700">
        <div className="container mx-auto px-4 py-16">
          <div className="max-w-4xl mx-auto text-center text-white">
            <h1 className="text-4xl font-bold mb-4">
              College of Guidance and Counseling
            </h1>
            <p className="text-xl opacity-90">
              Preparing the next generation of counseling professionals
            </p>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-12">
        <div className="max-w-4xl mx-auto">
          {/* Overview Section */}
          <section className="mb-16">
            <div className="bg-white rounded-xl shadow-sm p-8">
              <h2 className="text-2xl font-bold mb-6">Program Overview</h2>
              <p className="text-gray-600 mb-6">
                The College of Guidance and Counseling offers comprehensive programs designed to 
                prepare counseling professionals. Learn more about our{' '}
                <InlineLink to="/academics/counseling/mission">mission</InlineLink> and{' '}
                <InlineLink to="/academics/counseling/approach">counseling approach</InlineLink>.
              </p>
            </div>
          </section>

          {/* Objectives Section */}
          <section className="mb-16">
            <h2 className="text-2xl font-bold mb-6">Program Objectives</h2>
            <div className="grid md:grid-cols-2 gap-6">
              {objectives.map((objective, index) => (
                <div key={index} className="bg-white rounded-xl shadow-sm p-8 relative overflow-hidden">
                  <div className="absolute top-0 left-0 w-2 h-full bg-primary-500"></div>
                  <div className="flex items-start gap-4">
                    <span className="text-3xl">{objective.icon}</span>
                    <div>
                      <h3 className="text-xl font-semibold mb-2 text-primary-600">
                        {objective.title}
                      </h3>
                      <p className="text-gray-600">{objective.description}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Admission Requirements Section */}
          <section className="mb-16">
            <h2 className="text-2xl font-bold mb-6">Admission Requirements</h2>
            <div className="bg-white rounded-xl shadow-sm overflow-hidden">
              <div className="divide-y divide-gray-200">
                {admissionRequirements.map((requirement, index) => (
                  <div key={index} className="p-6">
                    <div className="flex items-start gap-4">
                      <span className="text-3xl">{requirement.icon}</span>
                      <div>
                        <h3 className="text-xl font-semibold mb-2 text-gray-900">
                          {requirement.title}
                        </h3>
                        <ul className="space-y-2">
                          {requirement.requirements.map((req, idx) => (
                            <li key={idx} className="flex items-center text-gray-600">
                              <span className="w-2 h-2 bg-primary-500 rounded-full mr-3"></span>
                              {req}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Available Programs Section */}
          <section className="mb-16">
            <h2 className="text-2xl font-bold mb-6">Available Programs</h2>
            <div className="space-y-4">
              {availableDegrees.map((degree, index) => (
                <ProgramLink 
                  key={index}
                  title={degree.title}
                  description={degree.description}
                  path={degree.path}
                  icon={degree.icon}
                />
              ))}
            </div>
          </section>

          {/* CTA Section */}
          <section>
            <div className="bg-gradient-to-br from-primary-50 to-primary-100 rounded-xl p-8 text-center">
              <h2 className="text-2xl font-bold mb-4">Begin Your Journey in Counseling</h2>
              <p className="text-gray-600 mb-8 max-w-2xl mx-auto">
                Take the first step towards becoming a professional counselor.
              </p>
              <div className="flex flex-col sm:flex-row justify-center gap-4">
                <ButtonLink to="/application" primary>
                  Apply Now
                </ButtonLink>
                <ButtonLink to="/request" primary={false}>
                  Request Information
                </ButtonLink>
              </div>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default CollegeGuidanceCounseling; 